/**
 * Login flow — builds login/logout URLs and exchanges auth code for tokens.
 */
import { AUTH_CONFIG } from './config';
import { createPkceChallenge, consumePkceVerifier } from './pkce';

/** Build the Cognito login URL with PKCE challenge. */
export const buildLoginUrl = async (): Promise<string> => {
  const challenge = await createPkceChallenge();
  return (
    `https://${AUTH_CONFIG.domain}/oauth2/authorize?` +
    `client_id=${AUTH_CONFIG.clientId}` +
    `&response_type=code` +
    `&scope=${AUTH_CONFIG.scopes}` +
    `&redirect_uri=${encodeURIComponent(AUTH_CONFIG.redirectUri)}` +
    `&code_challenge=${challenge}` +
    `&code_challenge_method=S256`
  );
};

/** Build the Cognito logout URL. */
export const buildLogoutUrl = (): string =>
  `https://${AUTH_CONFIG.domain}/logout?` +
  `client_id=${AUTH_CONFIG.clientId}` +
  `&logout_uri=${encodeURIComponent(AUTH_CONFIG.logoutUri)}`;

/** Exchange an authorization code for tokens. Throws on failure. */
export const exchangeCode = async (code: string): Promise<string> => {
  const verifier = consumePkceVerifier();
  if (!verifier) {
    throw new Error('PKCE verifier not found — login flow may have been interrupted.');
  }

  const res = await fetch(`https://${AUTH_CONFIG.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: AUTH_CONFIG.clientId,
      code,
      redirect_uri: AUTH_CONFIG.redirectUri,
      code_verifier: verifier,
    }),
  });

  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status}`);
  }

  const data = await res.json();
  return data.id_token;
};
